import { collectComposerInlineTokens } from "@amoreg/shared/composerInlineTokens";
import { collectUserMentions } from "../../composer-user-mentions";
import { serializeUserMention } from "../../composer-user-mentions";
export { serializeUserMention, collectUserMentions } from "../../composer-user-mentions";
import { serializeComposerPolicyMention } from "@amoreg/shared/composerTrigger";
import type { OrganizationPerson } from "../../state/governance";
import type { SkillView } from "../../state/connections";
import { policyDisplayName, type Policy } from "../../state/policies";

export type ReviewMentionKind = "document" | "skill" | "person";
export type ReviewMentionOption = {
  id: string;
  kind: ReviewMentionKind;
  label: string;
  detail: string;
  text: string;
  person?: OrganizationPerson;
};
export function detectReviewMention(text: string, cursor: number) {
  const end = Math.max(0, Math.min(text.length, cursor));
  const match = /(?:^|\s)([/@$])([^\s/\[\]()@$]*)$/.exec(text.slice(0, end));
  if (!match) return null;
  const kind: ReviewMentionKind =
    match[1] === "/" ? "document" : match[1] === "$" ? "skill" : "person";
  return {
    kind,
    query: match[2] ?? "",
    rangeStart: end - match[0].trimStart().length,
    rangeEnd: end,
  };
}
export function reviewMentionOptions(
  kind: ReviewMentionKind,
  query: string,
  data: {
    policies: readonly Policy[];
    skills: readonly SkillView[];
    people: readonly OrganizationPerson[];
  },
): ReviewMentionOption[] {
  const options: ReviewMentionOption[] =
    kind === "document"
      ? data.policies.map((policy) => ({
          id: policy.id,
          kind,
          label: policyDisplayName(policy),
          detail: policy.filename,
          text: serializeComposerPolicyMention(policyDisplayName(policy), policy.id),
        }))
      : kind === "skill"
        ? data.skills
            .filter((skill) => /^[a-z][\w:-]*$/i.test(skill.slug))
            .map((skill) => ({
              id: skill.id,
              kind,
              label: skill.name,
              detail: skill.description,
              text: `$${skill.slug}`,
            }))
        : data.people.map((person) => ({
            id: person.userId,
            kind,
            label: person.name || person.email,
            detail: [person.email, person.jobTitle, person.department].filter(Boolean).join(" · "),
            text: serializeUserMention(person.name || person.email, person.userId),
            person,
          }));
  const terms = query.toLocaleLowerCase().trim().split(/\s+/);
  return options
    .filter((option) =>
      terms.every((term) =>
        `${option.label} ${option.detail} ${option.text}`.toLocaleLowerCase().includes(term),
      ),
    )
    .slice(0, 30);
}

/** A compact draft preview should show labels, never the serialized identifiers. */
export function reviewMentionPreview(text: string): string {
  const tokens = [
    ...collectUserMentions(`${text} `),
    ...collectComposerInlineTokens(`${text} `),
  ].sort((a, b) => a.start - b.start);
  let cursor = 0;
  let result = "";
  for (const token of tokens) {
    if (token.start < cursor) continue;
    result += text.slice(cursor, token.start);
    result +=
      token.type === "user"
        ? `@${token.label}`
        : token.type === "policy"
          ? token.label
          : token.type === "skill"
            ? `$${token.value}`
            : token.value;
    cursor = token.end;
  }
  return result + text.slice(cursor);
}
